import { createContext, ReactNode, useContext, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthProfileContext } from './AuthProfileProvider';
import { useFirm } from '../queries/use-firm';
import { DOMAIN } from '../config';
import { Firm } from '../types/firm';
import ErrorSplashScreen from '../pages/ErrorSplashScreen';
import { Spinner } from '@cimplico/components';

type ProfileFirm = { id: string; shortId: string };

type FirmProviderProps = {
    children: ReactNode | ReactNode[];
};

const FirmContext = createContext<Firm | undefined>(undefined);

function getInitialFirmId(shortId: string, firms: ProfileFirm[]): string {
    const firm = firms.find((f) => f.shortId === shortId);

    // If this function is running, we know that there is a matching firm
    return firm!.id;
}

function FirmProvider({ children }: FirmProviderProps): JSX.Element {
    const profile = useAuthProfileContext();
    const firms: ProfileFirm[] = profile[`${DOMAIN}/workpapers`].firms || [];
    const { firmShortId } = useParams();
    const [firmId] = useState(() => getInitialFirmId(firmShortId as string, firms));
    const navigate = useNavigate();

    const firmQuery = useFirm(firmId);

    if (firmQuery.isLoading) {
        return (
            <div className="absolute w-full h-[var(--remaining-screen-height)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (firmQuery.isError || !firmQuery.isSuccess) {
        if (firmQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="Firm not found."
                    handleClick={(): void => navigate('/onboarding?step=join-firm')}
                    leftArrow={true}
                />
            );
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                handleClick={(): void => navigate('/onboarding?step=join-firm')}
                leftArrow={true}
            />
        );
    }

    return <FirmContext.Provider value={firmQuery.data.firm}>{children}</FirmContext.Provider>;
}

function useFirmContext(): Firm {
    const context = useContext(FirmContext);
    if (context === undefined) {
        throw new Error('useFirmContext must be used within a FirmProvider');
    }
    return context;
}

export { FirmProvider, useFirmContext };
